import { useEffect, useState, useMemo, useRef } from "react";
import { T } from "@/components/T";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchContent, fetchGlossary, fetchTemples, fetchFestivalPages } from "@/lib/api";
import { CATEGORIES, CATEGORY_BY_TYPE, SERVICE_CATEGORIES, itemPath } from "@/lib/categories";
import { resolveImage, onImgError, PIC, HOME_TILES, imgSrc } from "@/lib/images";
import { useSettings } from "@/context/SettingsContext";
import { HERO_ART } from "@/lib/heroes";
import { UPCOMING } from "@/lib/upcoming";
import KnowledgeCard from "@/components/KnowledgeCard";
import HomePanchang from "@/components/HomePanchang";
import TodayStrip from "@/components/TodayStrip";
import SadhanaBanner from "@/components/SadhanaBanner";
import SourcesStrip from "@/components/SourcesStrip";
import PremiumBanner from "@/components/PremiumBanner";
import AppLaunchBanner from "@/components/AppLaunchBanner";
import { SectionHeading } from "@/components/SectionHeading";
import { MandalaMotif, Divider } from "@/components/MotifBackground";
import EventsStrip from "@/components/EventsStrip";
import AnnouncementsStrip from "@/components/AnnouncementsStrip";
import Seo from "@/components/Seo";
import TodaysDeviForm from "@/components/TodaysDeviForm";
import AudioFeature from "@/components/AudioFeature";
import HomeAudioLive from "@/components/HomeAudioLive";
import DeviFormOfDay from "@/components/DeviFormOfDay";
import FestivalCountdown from "@/components/FestivalCountdown";
import FestivalPromo from "@/components/FestivalPromo";
import { ArrowRight, Search, Clock, Sparkles, Flame, Star, Calculator, Home as HomeIcon, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

const SERVICE_ICONS = { Flame, Sparkles, Star, Calculator, Home: HomeIcon, CalendarDays, Clock };

// Editorial "gateway" tiles for the homepage explore grid — a clean, uniform
// grid (2 cols on mobile, 4 on desktop) so every tile shares the same format.
const GATEWAYS = [
  { to: "/deities", deva: "देवता", en: "Deities", img: HOME_TILES.deities },
  { to: "/mantras", deva: "मंत्र", en: "Mantras", type: "mantra", img: HOME_TILES.mantras },
  { to: "/stotras", deva: "स्तोत्र", en: "Stotras", type: "stotra", img: HOME_TILES.stotras },
  { to: "/rituals", deva: "पूजा विधि", en: "Rituals", type: "puja_vidhi", img: HOME_TILES["puja-vidhi"] },
  { to: "/temples", deva: "मंदिर", en: "Temples", img: HOME_TILES.temples },
  { to: "/scriptures", deva: "शास्त्र", en: "Scriptures", img: HOME_TILES.scriptures },
  { to: "/suktas", deva: "सूक्त", en: "Suktas", type: "sukta", img: HOME_TILES.suktas },
  { to: "/festivals", deva: "पर्व", en: "Festivals", type: "festival", img: HOME_TILES.festivals },
];

function GatewayTile({ g, index = 0 }) {
  return (
    <Link
      to={g.to}
      data-testid={`gateway-${g.to.replace(/\//g, "")}`}
      style={{ animationDelay: `${index * 0.05}s` }}
      className="fade-up group relative aspect-[4/5] overflow-hidden border border-line"
    >
      <img src={imgSrc(g.img, 640)} alt={g.en} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/5" />
      <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
        <p className="font-deva deva-lh text-2xl text-white md:text-3xl">{g.deva}</p>
        <p className="mt-0.5 font-serif-latin text-xs uppercase tracking-[0.14em] text-[var(--gold)] md:text-sm">{g.en}</p>
      </div>
    </Link>
  );
}

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24 ${className}`}>{children}</section>
);

// Responsive card slider: swipeable on phones (scroll-snap, peeking next card)
// AND a slider on desktop with hover arrows — always shows cards symmetrically
// (2 across on tablet, 3 across on desktop) with a thumbnail image on each card.
const RAIL_ITEM = "w-[82%] shrink-0 snap-start sm:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-3rem)/3)]";
function Rail({ items, renderItem }) {
  const ref = useRef(null);
  const [paused, setPaused] = useState(false);
  const scrollBy = (dir) => { const el = ref.current; if (el) el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: "smooth" }); };

  // Slow, pausable auto-advance so the page feels alive (only when it overflows;
  // paused on hover/touch and when the user prefers reduced motion).
  useEffect(() => {
    if (items.length <= 3) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => {
      const el = ref.current;
      if (!el || paused) return;
      const child = el.firstElementChild;
      const step = child ? child.getBoundingClientRect().width + 24 : el.clientWidth * 0.33;
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 8) el.scrollTo({ left: 0, behavior: "smooth" });
      else el.scrollBy({ left: step, behavior: "smooth" });
    }, 5000);
    return () => clearInterval(id);
  }, [items.length, paused]);

  return (
    <div className="group/rail relative mt-10" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div ref={ref} onTouchStart={() => setPaused(true)} className="no-scrollbar -mx-5 flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto px-5 pb-4 sm:gap-6 md:mx-0 md:px-0">
        {items.map((it, i) => (
          <div key={it.slug || i} className={RAIL_ITEM}>{renderItem(it)}</div>
        ))}
      </div>
      {items.length > 3 && (
        <>
          <button aria-label="Previous" data-testid="rail-prev" onClick={() => scrollBy(-1)} className="absolute -left-3 top-1/2 z-10 hidden -translate-y-1/2 rounded-full border border-line bg-[var(--surface)] p-2.5 text-ink opacity-0 shadow-md transition-all hover:border-saffron hover:text-saffron group-hover/rail:opacity-100 lg:block"><ChevronLeft className="h-5 w-5" /></button>
          <button aria-label="Next" data-testid="rail-next" onClick={() => scrollBy(1)} className="absolute -right-3 top-1/2 z-10 hidden -translate-y-1/2 rounded-full border border-line bg-[var(--surface)] p-2.5 text-ink opacity-0 shadow-md transition-all hover:border-saffron hover:text-saffron group-hover/rail:opacity-100 lg:block"><ChevronRight className="h-5 w-5" /></button>
        </>
      )}
    </div>
  );
}

function CategoryRow({ type, items }) {
  const { t, pick } = useLang();
  const cat = CATEGORY_BY_TYPE[type];
  const rows = items.filter((i) => i.type === type).slice(0, 10);
  if (rows.length === 0) return null;
  return (
    <Section className="border-t border-line">
      <SectionHeading
        overline={cat.en}
        titleDeva={cat.deva}
        title=""
        description={pick(cat.tagline_hi, cat.tagline_en)}
        action={
          <Link to={cat.path} className="link-underline inline-flex items-center gap-1.5 text-sm text-saffron">
            {t("view_all")} <ArrowRight className="h-4 w-4" />
          </Link>
        }
      />
      <Rail items={rows} renderItem={(it) => <KnowledgeCard item={it} showImage />} />
    </Section>
  );
}

export default function Home() {
  const { t, lang, pick } = useLang();
  const { home_images = {}, home_featured_count = 6, page_headings = {}, home_spotlight = {}, home_promos = [] } = useSettings();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [glossary, setGlossary] = useState([]);
  const [temples, setTemples] = useState([]);
  const [festPages, setFestPages] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    fetchContent({ limit: 200 }).then(setItems).catch(() => {});
    fetchGlossary().then(setGlossary).catch(() => {});
    fetchTemples().then(setTemples).catch(() => {});
    fetchFestivalPages(1).then((d) => setFestPages(d.items || [])).catch(() => {});
  }, []);

  const promos = useMemo(() => {
    const built = (home_promos || []).filter((p) => p && p.enabled !== false);
    const fp = (festPages || []).map((f) => ({
      id: f.slug, to: `/festival/${f.slug}`, enabled: true, order: (f.home_order ?? 50) + 100,
      title_en: f.title, title_hi: f.title_hi, subtitle_en: f.overline, subtitle_hi: f.overline_hi,
      desc_en: f.seo_description || f.intro, desc_hi: f.intro_hi, image: f.hero_image,
      start: f.countdown_enabled ? f.start : "", end: f.end,
    }));
    return [...built, ...fp].sort((a, b) => (a.order ?? 50) - (b.order ?? 50));
  }, [home_promos, festPages]);

  const counts = useMemo(() => {
    const c = {};
    items.forEach((i) => (c[i.type] = (c[i.type] || 0) + 1));
    return c;
  }, [items]);

  const featured = useMemo(() => items.filter((i) => i.featured).slice(0, Math.max(1, home_featured_count)), [items, home_featured_count]);
  const recent = useMemo(() => items.slice(0, 9), [items]);
  const spotlightPin = ((home_spotlight.slug || (page_headings["home.spotlight"] || {}).pinned_slug) || "").trim();
  const spotlight = useMemo(() => {
    const mode = home_spotlight.mode || (spotlightPin ? "pinned" : "auto");
    const byslug = (s) => items.find((i) => i.slug === s);
    // Scheduled: festival/date pins take priority, then the weekly rotation.
    if (mode === "scheduled") {
      const today = new Date().toISOString().slice(0, 10);
      const fest = (home_spotlight.festival_map || []).find((f) => f && f.date === today && f.slug);
      if (fest) { const it = byslug(fest.slug); if (it) return it; }
      const queue = (home_spotlight.schedule || []).filter(Boolean);
      if (queue.length) { const it = byslug(queue[Math.floor(Date.now() / 86400000) % queue.length]); if (it) return it; }
    } else if (mode === "pinned" || spotlightPin) {
      const list = (Array.isArray(home_spotlight.slugs) && home_spotlight.slugs.length ? home_spotlight.slugs : [spotlightPin]).filter(Boolean);
      if (list.length) {
        const it = byslug(list[Math.floor(Date.now() / 86400000) % list.length]);
        if (it) return it;
      }
    }
    // Fallback: rotate a katha / stotra / sukta by day.
    const pool = items.filter((i) => ["katha", "stotra", "sukta"].includes(i.type));
    if (!pool.length) return null;
    return pool[Math.floor(Date.now() / 86400000) % pool.length];
  }, [items, spotlightPin, home_spotlight]);

  // Admin-overridable homepage section headings (Admin → Page Titles → Homepage).
  const hs = (slot, def) => {
    const o = page_headings[`home.${slot}`] || {};
    const en = lang === "en";
    return {
      overline: o.overline || def.overline,
      title: (en ? o.title_en : (o.title_deva || o.title_en)) || def.title,
      titleDeva: (o.title_deva ?? def.titleDeva) || "",
      description: (en ? o.desc_en : (o.desc_deva || o.desc_en)) || def.description,
    };
  };

  const quickLinks = [
    { to: "/mantras", label: pick("मंत्र", "Mantras") },
    { to: "/suktas", label: pick("सूक्त", "Suktas") },
    { to: "/stotras", label: pick("स्तोत्र", "Stotras") },
    { to: "/kathas", label: pick("कथा", "Kathas") },
    { to: "/rituals", label: pick("पूजा विधि", "Rituals") },
    { to: "/panchang", label: pick("पंचांग", "Panchangam") },
  ];

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <>
      <Seo path="/" jsonLd={{ "@context": "https://schema.org", "@type": "WebSite", name: "Dharmartha", url: (process.env.REACT_APP_BACKEND_URL || ""), potentialAction: { "@type": "SearchAction", target: "/search?q={query}", "query-input": "required name=query" } }} />
      <TodayStrip />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-line bg-[var(--surface)]">
        <MandalaMotif className="pointer-events-none absolute -right-24 -top-16 hidden h-[520px] w-[520px] text-[var(--gold)] opacity-[0.06] md:block md:-right-10" />
        <MandalaMotif className="pointer-events-none absolute -bottom-40 -left-40 hidden h-[420px] w-[420px] text-[var(--saffron)] opacity-[0.04] md:block" />
        <div className="relative z-[2] mx-auto max-w-5xl px-5 pb-20 pt-20 text-center md:px-8 md:pb-28 md:pt-28">
          <div className="fade-up">
            <h1 className="font-deva text-6xl leading-none text-ink md:text-8xl">धर्मार्थ</h1>
            <p className="mt-3 font-serif-latin text-xl uppercase tracking-[0.32em] text-ink-2 md:text-2xl"><T>Dharmartha</T></p>
            <p className="mt-5 font-deva-body text-xl text-saffron md:text-2xl">धर्मो रक्षति रक्षितः</p>
            <p className="mt-1.5 font-serif-latin text-sm italic text-ink-3 md:text-base"><T>Dharmo Rakṣati Rakṣitaḥ — “Dharma protects those who protect it”</T></p>
          </div>
          <Divider className="my-9" />
          <h2 className="fade-up font-serif-latin text-3xl leading-tight text-ink md:text-5xl" style={{ animationDelay: "0.1s" }}>
            {t("hero_headline")}
          </h2>
          <p className="fade-up mx-auto mt-5 max-w-2xl text-base leading-relaxed text-ink-2 md:text-lg" style={{ animationDelay: "0.18s" }}>
            {t("hero_support")}
          </p>

          {/* Central search */}
          <form onSubmit={submitSearch} className="fade-up mx-auto mt-10 flex max-w-xl items-center gap-3 border-b-2 border-line pb-2 transition-colors focus-within:border-saffron" style={{ animationDelay: "0.26s" }}>
            <Search className="h-5 w-5 text-ink-3" />
            <input
              data-testid="hero-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_placeholder")}
              className="w-full bg-transparent py-2 font-deva-body text-lg text-ink placeholder:text-ink-3 focus:outline-none"
            />
          </form>

          <div className="fade-up mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row" style={{ animationDelay: "0.34s" }}>
            <Link
              to="/explore"
              data-testid="hero-cta-explore"
              className="inline-flex items-center gap-2 bg-[var(--saffron)] px-8 py-3.5 font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]"
            >
              {t("explore_knowledge")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/kathas"
              data-testid="hero-cta-katha"
              className="inline-flex items-center gap-2 border border-[var(--ink)] px-8 py-3.5 font-medium text-ink transition-colors hover:bg-ink hover:text-[var(--surface)]"
            >
              {t("begin_katha")}
            </Link>
          </div>

          {/* Quick navigation */}
          <div className="fade-up mt-10 flex flex-wrap items-center justify-center gap-2.5" style={{ animationDelay: "0.42s" }}>
            {quickLinks.map((ql) => (
              <Link
                key={ql.to}
                to={ql.to}
                data-testid={`hero-chip-${ql.to.slice(1)}`}
                className="rounded-full border border-line bg-[var(--surface)]/70 px-4 py-1.5 text-sm text-ink-2 backdrop-blur-sm transition-colors hover:border-[var(--saffron)] hover:text-saffron"
              >
                {ql.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <AppLaunchBanner />

      <HomePanchang />
      <SadhanaBanner />
      <SourcesStrip />

      <PremiumBanner className="py-10" />

      {/* EXPLORE DHARMARTHA — editorial gateways */}
      <Section id="explore">
        <SectionHeading
          {...hs("explore", { overline: t("explore_overline"), title: t("explore_dharmartha"), titleDeva: "", description: t("explore_desc") })}
          align="center"
          divider
        />
        <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {GATEWAYS.map((g, i) => (
            <GatewayTile key={g.to} g={{ ...g, img: home_images[g.to.slice(1)] || g.img }} index={i} />
          ))}
        </div>
      </Section>

      {/* UPCOMING EVENTS */}
      <EventsStrip />

      {/* ANNOUNCEMENTS */}
      <AnnouncementsStrip />

      {/* DHARMARTHA PREMIUM PROMO */}
      <Section className="border-t border-line">
        <Link to="/premium" data-testid="home-premium-promo" className="group relative block overflow-hidden border border-[var(--gold)]/40 bg-[var(--surface)] p-8 transition-all duration-500 hover:-translate-y-0.5 hover:border-[var(--gold)] hover:shadow-[0_16px_50px_rgba(176,141,87,0.16)] md:p-12">
          <MandalaMotif className="pointer-events-none absolute -right-16 -top-10 h-72 w-72 text-[var(--gold)] opacity-[0.08]" />
          <div className="relative z-[2] max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--gold-muted)] bg-surface/70 px-3 py-1 text-xs font-medium tracking-wide text-gold">
              <Sparkles className="h-3.5 w-3.5" /> {pick("शीघ्र आ रहा है", "Coming Soon")}
            </span>
            <h2 className="mt-4 font-serif-latin text-3xl text-ink md:text-4xl">{pick("धर्मार्थ प्रीमियम", "Dharmartha Premium")}</h2>
            <p className="mt-3 font-serif-latin text-lg italic text-gold">{pick("अपने धर्म को सीखने, आचरण करने एवं जीने का एक गहन मार्ग।", "A deeper way to learn, practice and live your Dharma.")}</p>
            <p className="mt-4 text-[15px] leading-relaxed text-ink-2">{pick("वैयक्तिक मार्गदर्शन, गहन ज्ञान, धर्मार्थ AI, उन्नत पंचांग एवं और भी बहुत कुछ। प्रतीक्षा-सूची में जुड़ें।", "Personalised guidance, deeper knowledge, Dharmartha AI, an advanced Panchangam and much more. Join the waitlist.")}</p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-saffron transition-transform group-hover:translate-x-0.5">{pick("अधिक जानें", "Explore Premium")} <ArrowRight className="h-4 w-4" /></span>
          </div>
        </Link>
      </Section>

      {/* ADMIN-CONTROLLED FESTIVAL PROMOS (show/hide/reorder + countdown via Admin → Homepage Festivals) */}
      {promos.length > 0 && (
        <Section className="border-t border-line">
          <div className="grid gap-6" data-testid="home-festival-promos">
            {promos.map((p, i) => <FestivalPromo key={p.id} p={p} flip={i % 2 === 1} />)}
          </div>
        </Section>
      )}

      <TodaysDeviForm />
      <DeviFormOfDay />
      <AudioFeature placement="home" />
      <HomeAudioLive />

      {spotlight && (
        <Section className="border-t border-line">
          <SectionHeading {...hs("spotlight", { overline: t("spotlight_overline"), title: t("spotlight_title"), titleDeva: "", description: t("spotlight_desc") })} align="center" divider />
          <div className="mt-10 mx-auto max-w-2xl">
            <FeaturedCard item={spotlight} large />
          </div>
        </Section>
      )}

      {/* TEMPLES OF BHARAT */}
      {temples.length > 0 && (
        <Section className="border-t border-line bg-[var(--surface)]">
          <div className="flex items-end justify-between gap-4">
            <SectionHeading {...hs("temples", { overline: t("temples_overline"), title: t("temples_title"), titleDeva: "", description: "" })} />
            <Link to="/temples" data-testid="home-temples-link" className="link-underline hidden shrink-0 text-sm text-saffron sm:inline">{t("view_all")} →</Link>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {temples.slice(0, 3).map((tp) => (
              <Link key={tp.slug} to={`/temples/${tp.slug}`} className="group block overflow-hidden border border-line bg-[var(--surface)] transition-colors hover:border-[var(--gold)]">
                <div className="relative h-52 overflow-hidden bg-parchment">
                  <img src={imgSrc(resolveImage(tp), 640)} alt={tp.name} loading="lazy" decoding="async" onError={onImgError(tp)} className="h-full w-full object-contain object-center transition-transform duration-700 group-hover:scale-[1.03]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                  <p className="absolute bottom-3 left-4 font-deva text-lg text-white/90 deva-lh">{tp.deva_name}</p>
                </div>
                <div className="p-5">
                  <h3 className="font-serif-latin text-lg text-ink">{tp.name}</h3>
                  <p className="mt-1.5 text-sm text-ink-3">{tp.deity} · {tp.location}, {tp.state}</p>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* DHARMA QUIZ CTA */}
      <Section className="border-t border-line">
        <div className="relative overflow-hidden border border-line bg-[var(--surface-alt)]/40 px-6 py-12 text-center md:px-12 md:py-16">
          <MandalaMotif className="pointer-events-none absolute -right-10 -top-10 h-64 w-64 text-[var(--gold)] opacity-[0.07]" />
          <p className="overline-label text-gold">{t("quiz_overline")}</p>
          <h2 className={`mt-3 ${lang === "en" ? "font-serif-latin" : "font-deva"} text-3xl text-ink deva-lh md:text-4xl`}>{t("quiz_title")}</h2>
          <p className="mx-auto mt-4 max-w-xl font-serif-latin text-lg text-ink-2">{t("quiz_desc")}</p>
          <Link to="/quiz" data-testid="home-quiz-link" className="mt-8 inline-flex items-center gap-2 bg-[var(--saffron)] px-8 py-3.5 font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
            {t("quiz_cta")}
          </Link>
        </div>
      </Section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <Section className="border-t border-line bg-[var(--surface)]">
          <SectionHeading {...hs("featured", { overline: t("featured_overline"), title: t("featured_knowledge"), titleDeva: "", description: "" })} align="center" divider />
          <Rail items={featured} renderItem={(it) => <FeaturedCard item={it} />} />
        </Section>
      )}

      {/* CATEGORY ROWS */}
      <CategoryRow type="katha" items={items} />
      <CategoryRow type="mantra" items={items} />
      <CategoryRow type="puja_vidhi" items={items} />
      <CategoryRow type="festival" items={items} />

      {/* SERVICES & JYOTISH */}
      <Section className="border-t border-line">
        <SectionHeading
          {...hs("services", { overline: t("services_overline"), title: t("services_title"), titleDeva: "", description: t("services_desc") })}
          action={
            <Link to="/services" className="link-underline inline-flex items-center gap-1.5 text-sm text-saffron">
              {t("view_all")} <ArrowRight className="h-4 w-4" />
            </Link>
          }
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICE_CATEGORIES.map((s, i) => {
            const Icon = SERVICE_ICONS[s.icon] || Sparkles;
            return (
              <Link
                key={s.kind}
                to={`/services/${s.kind}`}
                data-testid={`home-service-${s.kind}`}
                style={{ animationDelay: `${i * 0.05}s` }}
                className="fade-up group relative flex flex-col overflow-hidden rounded-xl border border-[var(--gold)]/25 bg-surface p-6 shadow-[0_6px_24px_rgba(44,38,33,0.05)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--gold)]/50 hover:shadow-[0_16px_44px_rgba(199,80,0,0.12)]"
              >
                <div className="mb-5 flex items-center justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[rgba(199,80,0,0.12)] to-[rgba(184,134,11,0.15)] text-saffron ring-1 ring-[var(--gold)]/25 transition-transform duration-300 group-hover:scale-105">
                    <Icon className="h-5 w-5" />
                  </span>
                  <ArrowRight className="h-5 w-5 text-ink-3 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-saffron" />
                </div>
                <p className={`${lang === "en" ? "font-serif-latin" : "font-deva"} text-xl text-ink deva-lh`}>{pick(s.deva, s.en)}</p>
                <p className="tg-explainer mt-2.5 flex-1 text-sm leading-relaxed text-ink-2">{lang === "en" ? s.desc_en : s.desc_hi}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 border-t border-[var(--gold)]/20 pt-4 text-sm font-medium text-saffron">
                  {pick("अनुरोध करें", "Request")} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* GLOSSARY */}
      <Section className="border-t border-line bg-[var(--surface)]">
        <SectionHeading
          {...hs("glossary", { overline: t("glossary_overline"), title: t("explore_glossary"), titleDeva: "शब्दकोश", description: t("glossary_desc") })}
          action={
            <Link to="/glossary" className="link-underline inline-flex items-center gap-1.5 text-sm text-saffron">
              {t("view_all")} <ArrowRight className="h-4 w-4" />
            </Link>
          }
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {glossary.slice(0, 6).map((g) => (
            <Link
              key={g.id}
              to={`/glossary?q=${encodeURIComponent(g.transliteration)}`}
              className="group border border-line bg-parchment p-5 transition-colors hover:border-[var(--gold-muted)]"
            >
              <div className="flex items-baseline gap-3">
                <span className="font-deva text-2xl text-ink">{g.devanagari}</span>
                <span className="text-sm text-gold">{g.transliteration}</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-ink-2 line-clamp-2">{g.meaning}</p>
            </Link>
          ))}
        </div>
      </Section>

      {/* RECENTLY ADDED */}
      {recent.length > 0 && (
        <Section className="border-t border-line">
          <SectionHeading {...hs("recent", { overline: t("recent_overline"), title: t("recently_added"), titleDeva: "", description: "" })} align="center" divider />
          <Rail items={recent} renderItem={(it) => <KnowledgeCard item={it} showImage />} />
        </Section>
      )}

      {/* COMING SOON TEASER */}
      <Section className="border-t border-line">
        <div className="relative overflow-hidden border border-line bg-[var(--surface-alt)]/40 px-6 py-10 md:px-12 md:py-12">
          <MandalaMotif className="pointer-events-none absolute -right-12 -top-12 hidden h-56 w-56 text-[var(--gold)] opacity-[0.07] md:block" />
          <div className="relative z-[2] flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="max-w-xl">
              <p className="overline-label text-gold">{pick("शीघ्र आ रहा है", "Coming Soon")}</p>
              <h2 className="mt-2 font-serif-latin text-2xl text-ink md:text-3xl">{pick("धर्मार्थ पर शीघ्र आ रहा है", "Coming soon to Dharmartha")}</h2>
              <p className="mt-3 text-sm leading-relaxed text-ink-2">{pick("पाठ्यक्रम, मंदिरों से सीधा प्रसारण, दैनिक पूजा, जप लाइव एवं बहुत कुछ — श्रद्धा एवं प्रामाणिकता के साथ तैयार किया जा रहा है।", "Courses, live darshan from temples, daily pooja, Japa Live and much more — being built with care and authenticity.")}</p>
              <Link to="/coming-soon" data-testid="home-coming-soon-link" className="link-underline mt-5 inline-flex items-center gap-1.5 text-sm text-saffron">
                {pick("रोडमैप देखें", "See the roadmap")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-2 md:max-w-sm md:justify-end">
              {UPCOMING.slice(0, 6).map((u) => (
                <Link key={u.slug} to={u.path} className="rounded-full border border-line bg-surface px-3 py-1.5 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
                  {pick(u.hi, u.en)}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ABOUT TEASER */}
      <Section className="border-t border-line bg-[var(--surface)]">
        <div className="grid items-center gap-12 md:grid-cols-2">
          <div>
            <p className="overline-label mb-3">{t("about_dharmartha")}</p>
            <h2 className="font-serif-latin text-3xl leading-tight text-ink md:text-4xl">
              {t("about_title")}
            </h2>
            <p className="mt-5 max-w-lg leading-relaxed text-ink-2">
              {t("about_body")}
            </p>
            <Link
              to="/about"
              className="mt-7 inline-flex items-center gap-2 border border-[var(--ink)] px-7 py-3 text-sm font-medium text-ink transition-colors hover:bg-ink hover:text-[var(--surface)]"
            >
              {t("about_cta")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="relative">
            <img
              src={imgSrc("https://static.prod-images.emergentagent.com/jobs/f79bd64e-30d1-4119-8097-970fcb1907b0/images/c332f168c204d952d0c6a6d2c551fccd0291a78361a35f6ba15f0ef48a89b469.jpeg", 700)}
              alt="Devanagari palm-leaf manuscript"
              className="h-[360px] w-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <MandalaMotif className="absolute -bottom-8 -left-8 h-40 w-40 text-[var(--gold)] opacity-20" />
          </div>
        </div>
      </Section>
    </>
  );
}

function FeaturedCard({ item, large, className = "" }) {
  const { t, lang, pick, pickTitle, pickDesc } = useLang();
  const cat = CATEGORY_BY_TYPE[item.type];
  const title = pickTitle(item);
  return (
    <Link
      to={itemPath(item)}
      data-testid={`featured-card-${item.slug}`}
      className={`group flex h-full flex-col overflow-hidden border border-line bg-parchment transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,38,33,0.08)] ${className}`}
    >
      <div className="overflow-hidden bg-parchment aspect-[16/9]">
        <img src={imgSrc(resolveImage(item), 700)} alt={title} loading="lazy" decoding="async" onError={onImgError(item)} className="h-full w-full object-contain object-center transition-transform duration-700 group-hover:scale-[1.03]" />
      </div>
      <div className="flex flex-1 flex-col p-6 md:p-8">
        <span className="overline-label text-gold">{pick(cat?.deva, cat?.en)}</span>
        <h3 className={`mt-3 ${lang === "en" ? "font-serif-latin" : "font-deva"} text-ink deva-lh text-2xl`}>{title}</h3>
        <p className="mt-3 flex-1 leading-relaxed text-ink-2 line-clamp-3">{pickDesc(item)}</p>
        <div className="mt-5 flex items-center gap-4 border-t border-line pt-4 text-xs text-ink-3">
          {item.read_time && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{item.read_time}</span>}
          <span className="ml-auto inline-flex items-center gap-1.5 text-sm text-saffron">{t("read")} <ArrowRight className="h-4 w-4" /></span>
        </div>
      </div>
    </Link>
  );
}
